import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import SectionContainer from '@components/Layout/SectionContainer/SectionContainer'
import { setCtaTemplate } from '@/app/redux/actions/activity-creation'
import { useEffect, useState } from 'react'
import { Album, errorsSelectors, SmartlyActionItems } from '@Techedge/digitalportal-shared'
import { albumCreationCta } from '@/app/models/new-activity-creation-cta'
import { CtaGroupWrapper } from '../UI/CtaGroup/CtaGroupWrapper'
import { FILTERS_REDUCER_NAMESPACE, StoresOptionsList } from '@/app/redux/reducers/filters'
import FilterSubCategoryItem from '../Filters/FilterSubCategoryItem/FilterSubCategoryItem'
import {
    getFiltersDomain,
    getMacroareas,
    getStoresList,
    getVisibilities,
} from '@/app/redux/actions/filters'
import { LuxDatePicker, LuxSelect } from '@lux/wardrobe-lib'
import { AlbumsActions, editAlbum, previewAlbum, saveNewAlbum } from '@/app/redux/actions/albums'
import { ProjectsService } from '@/app/services/ProjectsService'
import AlbumsDistribution from './AlbumsDistribution'
import useDistributionAction from '@/app/hooks/useDistributionAction'
import { DistributionListProps } from '@/app/models/new-resource'
import { FiltersService } from '@/app/services/FiltersService'
import { TypeOfSubcategory } from '@/app/models/filters'

const NewAlbumAccordion = (props: {
    setCtaTemplate: (ReactElement) => void
    storesOptionList: StoresOptionsList
    getFiltersDomain: () => void
    albumDetails: Album
    setAlbumDetails: (a: Album) => void
    saveNewAlbum: (albumData: any, successMessage: string) => void
    saveEditedAlbum: (albumData: any, successMessage: string) => void
    getMacroareas: (banner: string[]) => void
    macroareas: { value: string; label: string }[]
    storesList: any[]
    userSearchError: (error: any) => void
    visibilityList: any[]
    getVisibilities: () => void
    albumId?: string
    getStoresList: () => void
    showAlbumPreview: (albumData: any) => void
    disabled?: boolean
}) => {
    const {
        setCtaTemplate,
        storesOptionList,
        getFiltersDomain,
        getMacroareas,
        macroareas,
        albumDetails,
        userSearchError,
        setAlbumDetails,
        saveNewAlbum,
        saveEditedAlbum,
        storesList,
        visibilityList,
        getVisibilities,
        albumId,
        getStoresList,
        showAlbumPreview,
        disabled,
    } = props

    const intl = useIntl()
    const componentClass = 'smart-new-activity-news smart-new-album'
    const [distributionAction] = useDistributionAction()

    const [pictureStatusList, setPictureStatusList] = useState([])
    const [storesSuggestions, setStoresSuggestions] = useState([])
    const [projectSuggestions, setProjectSuggestions] = useState([])
    const [countryOptions, setCountryOptions] = useState(storesOptionList.country)
    const [activityTypes, setActivityTypes] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        FiltersService.GetPictureStatusList().then((res) => {
            let psList = res.data?.pictureStatus?.map((ps) => ({ value: ps, label: ps }))
            setPictureStatusList(psList)
        })
    }, [])

    useEffect(() => {
        setStoresSuggestions(storesList.slice(0, 5))
    }, [storesList])

    useEffect(() => {
        // filter country options based on selected macroregions
        if (albumDetails.filters?.macroRegions?.length > 0) {
            let _countryOptions = []
            albumDetails.filters?.macroRegions.forEach((mr) => {
                let _found = storesOptionList.coneOfVision.filter((c) => c.macroregion === mr)
                _found.forEach((t) => {
                    if (!_countryOptions.includes(t.country)) _countryOptions.push(t.country)
                })
                setCountryOptions(
                    _countryOptions
                        .map((c) => ({ value: c, label: c }))
                        .sort((a, b) => a.value.localeCompare(b.value))
                )
            })
            if (albumDetails.filters?.countries?.length > 0) {
                let _selectedCountries = albumDetails.filters?.countries.filter((c) =>
                    _countryOptions.includes(c)
                )

                onChangeFilter(_selectedCountries, 'countries')
            }
        } else {
            setCountryOptions(storesOptionList.country)
        }
    }, [albumDetails.filters?.macroRegions, storesOptionList.macroRegion])

    const filterStoreNameSuggestions = (val: string) => {
        setIsLoading(true)
        FiltersService.GetStoreList({
            mode: 'standard',
            coneOfVision: true,
            groupedFields: 'all',
            tableFilters: { search: val },
        })
            .then((res) => {
                setStoresSuggestions(res.data.stores)
            })
            .finally(() => setIsLoading(false))
    }

    const filterProjectSuggestions = (val: string) => {
        setIsLoading(true)
        ProjectsService.SearchProject(val, '', ['id', 'project_name'])
            .then((res) => {
                setProjectSuggestions(res.data.activities)
            })
            .finally(() => setIsLoading(false))
    }

    const ctaHandler = async (action: string) => {
        switch (action) {
            case AlbumsActions.SAVE_NEW_ALBUM:
                saveNewAlbum(
                    albumDetails,
                    intl.formatMessage({
                        id: 'ph2.modals.album_saved_message',
                        defaultMessage: 'Album saved successfully',
                    })
                )
                break
            case AlbumsActions.SAVE_EDITED_ALBUM:
                saveEditedAlbum(
                    albumDetails,
                    intl.formatMessage({
                        id: 'ph2.modals.album_edited_message',
                        defaultMessage: 'Album edited successfully',
                    })
                )
                break
            case AlbumsActions.SHOW_ALBUM_PREVIEW:
                showAlbumPreview(albumDetails)
                break
        }
    }

    const onChangeFilter = (value: any, key: string) => {
        let _value = value
        if (key == 'activityTypes') {
            _value = [value]
        } else if (key === 'selectedProjects') {
            _value = value[0]?.id === '*' ? projectSuggestions : value
        }
        let tmpAlbumDetails = {
            ...albumDetails,
            filters: { ...albumDetails.filters, [key]: _value },
        }
        setAlbumDetails(tmpAlbumDetails)
    }

    const isFormValid = () => {
        return !!albumDetails.name && albumDetails.distribution?.stores?.length > 0
    }

    useEffect(() => {
        getFiltersDomain()
        getStoresList()
        getVisibilities()
        FiltersService.GetActivityTypes({
            projectTypesNames: [SmartlyActionItems.VISIT, SmartlyActionItems.TASK],
        }).then((res) => {
            if (res.data?.filterProjectTypes) {
                let tmpActivityTypes = []
                res.data.filterProjectTypes.forEach((type) => {
                    type.subTypes?.forEach((subType) => {
                        tmpActivityTypes.push({
                            value: {
                                type: type.projectTypeName,
                                subType: subType.projectSubTypeName,
                            },
                            label: intl.formatMessage({
                                id: `modals.new.activity.type.${subType.projectSubTypeName}`,
                                defaultMessage: subType.projectSubTypeName,
                            }),
                        })
                    })
                })
                setActivityTypes(tmpActivityTypes)
            }
        })
    }, [])

    useEffect(() => {
        if (distributionAction.distributionModel.selectedStoreList.value.length == 0) {
            distributionAction.setDistributionModel({
                ...distributionAction.distributionModel,
                selectedStoreList: {
                    ...distributionAction.distributionModel.selectedStoreList,
                    value: albumDetails.distribution.stores,
                },
            })
        }
    }, [albumDetails?.id])

    const distributionChangeHandler = (form: DistributionListProps) => {
        distributionAction.setDistributionModel(form)
        let tmpAlbumDetails = {
            ...albumDetails,
            distribution: { ...albumDetails.distribution, stores: form.selectedStoreList.value },
        }
        setAlbumDetails(tmpAlbumDetails)
    }

    useEffect(() => {
        if (storesOptionList.banner.length > 0) {
            getMacroareas(storesOptionList.banner.map((b) => b.value))
        }
    }, [storesOptionList.banner])

    useEffect(() => {
        if (!disabled) {
            // PERMISSION CTA
            const ctaConfig = albumCreationCta(SmartlyActionItems.ALBUM, {
                isEdit: albumId ? true : false,
            }).setDisableConditions([
                {
                    action: AlbumsActions.SAVE_NEW_ALBUM,
                    disableCondition: !isFormValid(),
                },
                {
                    action: AlbumsActions.SAVE_EDITED_ALBUM,
                    disableCondition: !isFormValid(),
                },
                {
                    action: AlbumsActions.SHOW_ALBUM_PREVIEW,
                    disableCondition: !isFormValid(),
                },
            ])
            setCtaTemplate(
                <CtaGroupWrapper
                    ctaConfig={ctaConfig}
                    componentClass={componentClass}
                    ctaHandler={(action) => ctaHandler(action)}
                />
            )
        }
    }, [albumDetails, albumDetails.distribution, albumDetails.filters])

    const filterItems = [
        {
            label: intl.formatMessage({
                id: 'modals.filters.label.macro_region',
                defaultMessage: 'Macro Region',
            }),
            type: 'MULTISELECT',
            filterPath: 'macroRegions',
            listOptions: storesOptionList.macroRegion,
            showBadge: albumDetails.filters?.macroRegions?.length > 0,
        },
        {
            label: intl.formatMessage({
                id: 'modals.filters.label.country',
                defaultMessage: 'Country',
            }),
            type: 'MULTISELECT',
            filterPath: 'countries',
            listOptions: countryOptions,
            showBadge: albumDetails.filters?.countries?.length > 0,
        },
        {
            label: intl.formatMessage({
                id: 'modals.filters.label.banner',
                defaultMessage: 'Banner',
            }),
            type: 'MULTISELECT',
            filterPath: 'banners',
            listOptions: storesOptionList.banner,
            showBadge: albumDetails.filters?.banners?.length > 0,
        },
        {
            label: intl.formatMessage({
                id: 'ph2.modals.filters.label.store_name',
                defaultMessage: 'Store Name',
            }),
            type: 'AUTOCOMPLETE_MULTISELECT',
            filterPath: 'storeIds',
            listOptions: storesSuggestions, // TODO
            setListOptions: filterStoreNameSuggestions,
            optionLabel: 'store_name',
            optionValue: 'store_id',
            showBadge: albumDetails.filters?.storeIds?.length > 0,
            isLoading,
        },

        {
            label: intl.formatMessage({
                id: 'modals.filters.label.segment',
                defaultMessage: 'Segment',
            }),
            type: 'MULTISELECT',
            filterPath: 'segments',
            listOptions: storesOptionList.segment,
            showBadge: albumDetails.filters?.segments?.length > 0,
        },
        {
            label: intl.formatMessage({
                id: 'ph2.modals.filters.label.macroarea',
                defaultMessage: 'Macro Area',
            }),
            type: 'MULTISELECT',
            filterPath: 'macroAreas',
            listOptions: macroareas,
            showBadge: albumDetails.filters?.macroAreas?.length > 0,
        },
        {
            label: intl.formatMessage({
                id: 'modals.new.activity.type.title',
                defaultMessage: 'Activity type',
            }),
            type: 'CUSTOM',
            filterPath: 'activityTypes',
            showBadge: albumDetails.filters?.activityTypes?.length > 0,
            customContent: (
                <LuxSelect
                    value={
                        albumDetails.filters?.activityTypes?.length > 0
                            ? albumDetails.filters?.activityTypes[0]
                            : undefined
                    }
                    onChangeCallback={(val) => {
                        onChangeFilter(val, 'activityTypes')
                    }}
                    placeholder="Select activity type"
                    options={activityTypes}
                    disabled={disabled}
                />
            ),
        },
        {
            label: intl.formatMessage({
                id: 'modals.filters.label.from_to',
                defaultMessage: 'From - to',
            }),
            type: 'CUSTOM',
            filterPath: 'dateRange',
            customContent: (
                <div>
                    <LuxDatePicker
                        label="From date"
                        value={albumDetails.filters?.from}
                        onChangeCallback={(val) => onChangeFilter(val, 'from')}
                        disabled={disabled}
                    />
                    <LuxDatePicker
                        label="To date"
                        containerClasses="mt-2"
                        value={albumDetails.filters?.to}
                        onChangeCallback={(val) => onChangeFilter(val, 'to')}
                        disabled={disabled}
                    />
                </div>
            ),
            showBadge: !!(albumDetails.filters?.from || albumDetails.filters?.to),
        },
        {
            label: intl.formatMessage({
                id: 'modals.new.activity.section.main.info.project.title',
                defaultMessage: 'Project title',
            }),
            type: 'AUTOCOMPLETE_MULTISELECT',
            filterPath: 'selectedProjects',
            optionLabel: 'project_name',
            optionValue: 'id',
            listOptions: projectSuggestions,
            setListOptions: filterProjectSuggestions,
            showBadge: albumDetails.filters?.selectedProjects?.length > 0,
            minDigit: 3,
            isLoading,
        },
        {
            label: intl.formatMessage({
                id: 'ph2.modals.filters.label.picture_status',
                defaultMessage: 'Picture status',
            }),
            type: 'MULTISELECT',
            filterPath: 'pictureStatus',
            showBadge: albumDetails.filters?.pictureStatus?.length > 0,
            listOptions: pictureStatusList, // TODO
        },
        ...(disabled
            ? []
            : [
                  {
                      label: intl.formatMessage({
                          id: 'modals.new.activity.section.distribution',
                          defaultMessage: 'Distribution',
                      }),
                      filterPath: 'distribution',
                      type: 'CUSTOM',
                      customContent: (
                          <AlbumsDistribution
                              albumDetails={albumDetails}
                              visibilityList={visibilityList}
                              setAlbumDetails={setAlbumDetails}
                              userSearchError={userSearchError}
                              distributionAction={distributionAction}
                              distributionChangeHandler={distributionChangeHandler}
                          />
                      ),
                      showBadge: albumDetails.distribution?.stores?.length > 0,
                  },
              ]),
    ]

    return (
        <div className={`${componentClass} container`}>
            <SectionContainer>
                {filterItems.map((item, idx) => (
                    <FilterSubCategoryItem
                        disabled={disabled}
                        key={idx}
                        {...item}
                        type={item.type as TypeOfSubcategory}
                        filterValue={
                            albumDetails.filters ? albumDetails.filters[item.filterPath] : undefined
                        }
                        setFilterValue={(value) => onChangeFilter(value, item.filterPath)}
                    />
                ))}
            </SectionContainer>
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    storesOptionList: state[FILTERS_REDUCER_NAMESPACE]?.storesOptionList ?? [],
    macroareas:
        state[FILTERS_REDUCER_NAMESPACE]?.macroareas.map((m) => ({ value: m, label: m })) ?? [],
    storesList: state[FILTERS_REDUCER_NAMESPACE]?.distributionList?.stores ?? [],
    visibilityList: state[FILTERS_REDUCER_NAMESPACE].visibilityList,
    albumId: state.layoutReducer.modals.props?.id,
})

const mapDispatchToProps = (dispatch: any) => ({
    getFiltersDomain: () => dispatch(getFiltersDomain()),
    getStoresList: () => dispatch(getStoresList(true)),
    getMacroareas: (banner: string[]) => dispatch(getMacroareas(banner)),
    setCtaTemplate: (template) => dispatch(setCtaTemplate(template)),
    saveNewAlbum: (albumData: any, successMessage: string) => {
        dispatch(saveNewAlbum(albumData, successMessage))
    },
    saveEditedAlbum: (albumData: any, successMessage: string) => {
        dispatch(editAlbum(albumData, successMessage))
    },
    showAlbumPreview: (albumData: any) => dispatch(previewAlbum(albumData)),
    userSearchError: (error: any) => dispatch(errorsSelectors.createErrorMessageSelector(error)),
    getVisibilities: () => {
        dispatch(getVisibilities())
    },
})

export default connect(mapStateToProps, mapDispatchToProps)(NewAlbumAccordion)
